import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import FolkSaptapurHeader from './FolkSaptapurHeader';
import FolkSaptapurTabs from './FolkSaptapurTabs';
import { folkSaptapurAPI } from '../../services/api';

const CATEGORY_LABEL = { weekly: 'Tasks on Specific Day', monthly: 'Regular Tasks' };

function weeklyTaskText(task) {
  return `${task.text} (${task.task_days.join(', ')})`;
}

function monthlyTaskText(cat) {
  if (cat.tasks.length === 0) return '—';
  return cat.tasks.map((t) => t.text).join('; ');
}

function ServiceTasksOverview() {
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const response = await folkSaptapurAPI.getServiceTasksOverview();
      setStudents(response.data.students);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load service tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Only students with at least one assigned task show up here at all -
  // and among those, students with a task on a specific day are listed first.
  const hasAnyTask = (s) => s.weekly.tasks.length > 0 || s.monthly.tasks.length > 0;
  const assignedStudents = students.filter(hasAnyTask);
  const visibleStudents = [
    ...assignedStudents.filter((s) => s.weekly.tasks.length > 0),
    ...assignedStudents.filter((s) => s.weekly.tasks.length === 0),
  ];

  return (
    <Box>
      <FolkSaptapurHeader />
      <FolkSaptapurTabs />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Service Tasks
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Tasks are configured on the Assign Service Tasks tab. Each task on a specific day gets its own line
          so nothing gets cut off.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Sl No</strong></TableCell>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell><strong>Task Type</strong></TableCell>
                <TableCell><strong>Service Tasks</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && visibleStudents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    {students.length === 0
                      ? 'No students yet — add students on the Assign Service Tasks tab.'
                      : 'No students have any tasks assigned yet — assign one on the Assign Service Tasks tab.'}
                  </TableCell>
                </TableRow>
              )}
              {visibleStudents.map((s, idx) => {
                const weeklyTasks = s.weekly.tasks;
                const weeklyRowCount = Math.max(weeklyTasks.length, 1);
                const totalRows = weeklyRowCount + 1; // + the Regular Tasks row

                const rows = [];
                for (let i = 0; i < weeklyRowCount; i++) {
                  const task = weeklyTasks[i];
                  rows.push(
                    <TableRow key={`${s.student_id}-weekly-${i}`}>
                      {i === 0 && (
                        <>
                          <TableCell rowSpan={totalRows} sx={{ verticalAlign: 'top' }}>{idx + 1}</TableCell>
                          <TableCell rowSpan={totalRows} sx={{ verticalAlign: 'top', fontWeight: 600 }}>{s.name}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                        {CATEGORY_LABEL.weekly}
                      </TableCell>
                      <TableCell sx={{ fontStyle: 'italic', color: task ? 'text.secondary' : 'text.disabled' }}>
                        {task ? weeklyTaskText(task) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                }

                rows.push(
                  <TableRow key={`${s.student_id}-monthly`} sx={{ '& td': { borderBottom: '2px solid', borderColor: 'text.primary' } }}>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                      {CATEGORY_LABEL.monthly}
                    </TableCell>
                    <TableCell sx={{ fontStyle: 'italic', color: s.monthly.tasks.length === 0 ? 'text.disabled' : 'text.secondary' }}>
                      {monthlyTaskText(s.monthly)}
                    </TableCell>
                  </TableRow>
                );

                return rows;
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
}

export default ServiceTasksOverview;
